/* global SideDrawer */

if (!customElements.get('facet-filters')) {
  class FacetFilters extends SideDrawer {
    close() {
      this.closeMobileFilterPanel();
      super.close();
    }

    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.historyChangeHandler);

      if (this.breakpointChangeHandler) {
        window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }
    }

    init() {
      this.filteringEnabled = this.dataset.filtering === 'true';
      this.sortingEnabled = this.dataset.sorting === 'true';
      this.form = document.getElementById('facets');
      this.results = document.getElementById('filter-results');
      this.expanded = [];
      this.filterChangeTimeout = null;

      this.handleBreakpointChange();
      this.addElements();
      this.addListeners();
      this.syncSaleToggleFromSearch(window.location.search);
      this.applySaleOnlyFilter(window.location.search);
      this.toggleNoResultsMessage();

      this.historyChangeHandler = this.historyChangeHandler || this.handleHistoryChange.bind(this);
      window.addEventListener('popstate', this.historyChangeHandler);
    }

    addElements() {
      if (this.filteringEnabled) {
        this.filters = this.querySelector('.facets__filters');
        this.activeFilters = this.querySelector('.facets__active-filters');
        this.activeFiltersList = this.querySelector('.active-filters');
        this.activeFiltersHeader = this.querySelector('.active-filters-header');
        this.footer = this.querySelector('.facets__footer');
        this.vendorSearchInputs = this.querySelectorAll('.js-vendor-filter-search');
        this.saleToggle = this.querySelector('.js-price-sale-toggle');
        this.mobileNav = this.querySelector('.js-facets-mobile-nav');
        this.mobileBackButton = this.querySelector('.js-facets-mobile-back');
        this.mobileTitle = this.querySelector('.js-facets-mobile-title');
        this.mobileClearAllButton = this.querySelector('.js-mobile-clear-all');
        this.mobileActiveFilterItem = null;
      }

      if (this.sortingEnabled) {
        this.desktopSortBy = document.querySelector('.products-toolbar__sort');
      }
    }

    addListeners() {
      this.handleFilterChangeBound = this.handleFilterChangeBound
        || this.handleFilterChange.bind(this);

      if (this.filteringEnabled) {
        this.breakpointChangeHandler = this.breakpointChangeHandler
          || this.handleBreakpointChange.bind(this);
        this.handleVendorSearchInputBound = this.handleVendorSearchInputBound
          || this.handleVendorSearchInput.bind(this);
        this.handleFiltersClickBound = this.handleFiltersClickBound
          || this.handleFiltersClick.bind(this);
        this.handleMobileFilterToggleCaptureBound = this.handleMobileFilterToggleCaptureBound
          || this.handleMobileFilterToggleCapture.bind(this);
        this.handleActiveFiltersClickBound = this.handleActiveFiltersClickBound
          || this.handleActiveFiltersClick.bind(this);
        this.handleMobileBackClickBound = this.handleMobileBackClickBound
          || this.handleMobileBackClick.bind(this);
        this.filters.removeEventListener('click', this.handleMobileFilterToggleCaptureBound, true);
        this.filters.addEventListener('click', this.handleMobileFilterToggleCaptureBound, true);
        this.filters.addEventListener('click', this.handleFiltersClickBound);
        if (this.form) {
          this.form.removeEventListener('input', this.handleFilterChangeBound);
          this.form.removeEventListener('change', this.handleFilterChangeBound);
          this.form.addEventListener('input', this.handleFilterChangeBound);
          this.form.addEventListener('change', this.handleFilterChangeBound);
        }
        if (this.mobileClearAllButton) {
          this.mobileClearAllButton.removeEventListener('click', this.handleFiltersClickBound);
          this.mobileClearAllButton.addEventListener('click', this.handleFiltersClickBound);
        }
        this.mobileBackButton?.addEventListener('click', this.handleMobileBackClickBound);
        if (this.activeFilters) {
          this.activeFilters.addEventListener('click', this.handleActiveFiltersClickBound);
        }
        this.vendorSearchInputs.forEach((input) => {
          input.addEventListener('input', this.handleVendorSearchInputBound);
        });
        window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }

      if (this.sortingEnabled) {
        if (this.desktopSortBy) {
          this.desktopSortBy.removeEventListener('change', this.handleFilterChangeBound);
          this.desktopSortBy.addEventListener('change', this.handleFilterChangeBound);
        }
      }
    }

    /**
     * Handles viewport breakpoint changes.
     */
    handleBreakpointChange() {
      if (theme.mediaMatches.lg) {
        this.closeMobileFilterPanel();
        this.setAttribute('open', '');
        this.setAttribute('aria-hidden', 'false');
        this.removeAttribute('aria-modal');
        this.removeAttribute('role');
      } else {
        this.closeMobileFilterPanel();
        this.close();
        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');
        this.setAttribute('aria-hidden', 'true');
        this.hidden = false;
      }
    }

    /**
     * Handles 'input' events on the filters and 'change' events on the sort by dropdown.
     * @param {object} evt - Event object.
     */
    handleFilterChange(evt) {
      if (evt.target.classList?.contains('js-vendor-filter-search')) return;
      if (evt.target.classList?.contains('js-filter-all-option')) {
        const clearUrl = evt.target.dataset.clearUrl;
        if (clearUrl) {
          evt.target.checked = true;
          this.applyFilters(new URL(clearUrl, window.location.origin).searchParams.toString(), evt);
        }
        return;
      }
      const currentFilter = evt.target.closest('.filter');
      const allOption = currentFilter?.querySelector('.js-filter-all-option');
      if (allOption) {
        allOption.checked = false;
      }
      if (evt.target.classList?.contains('js-product-type-option') && evt.target.checked) {
        const filter = evt.target.closest('.filter');
        if (filter) {
          filter.querySelectorAll('.js-product-type-option:checked').forEach((input) => {
            if (input !== evt.target) input.checked = false;
          });
          this.syncProductTypeActiveLabels(filter);
        }
      }

      // Only allow relevant 'change' events.
      if (evt.type === 'change' && !(evt.target.id?.includes('price-range')
        || evt.target.id?.includes('sort-by')
        || evt.target.classList?.contains('js-price-sale-toggle'))) return;

      // Dont reload when typing a price
      if (evt.target.id?.includes('price-range') && evt.constructor.name === 'InputEvent') return;

      const timeoutDelay = 500;

      clearTimeout(this.filterChangeTimeout);

      this.filterChangeTimeout = setTimeout(() => {
        const formData = new FormData(this.form);
        const searchParams = new URLSearchParams(formData);
        const emptyParams = [];

        if (this.sortingEnabled) {
          let currentSortBy = searchParams.get('sort_by');
          if (evt.detail?.selectedValue) {
            currentSortBy = evt.detail.selectedValue;
          } else if (evt.target?.name === 'sort_by' && evt.target?.value) {
            currentSortBy = evt.target.value;
          }

          // Ensure one authoritative sort_by param is sent.
          searchParams.delete('sort_by');
          if (currentSortBy) {
            searchParams.set('sort_by', currentSortBy);
          }
        }

        // Get empty parameters.
        searchParams.forEach((value, key) => {
          if (!value) emptyParams.push(key);
        });

        // Remove empty parameters.
        emptyParams.forEach((key) => {
          searchParams.delete(key);
        });

        this.applyFilters(searchParams.toString(), evt);
      }, timeoutDelay);
    }

    /**
     * Sync sale toggle checked state from URL search params.
     * @param {string} searchParams - URL search string or params string.
     */
    syncSaleToggleFromSearch(searchParams) {
      if (!this.saleToggle) return;
      const normalized = searchParams.startsWith('?') ? searchParams.slice(1) : searchParams;
      const params = new URLSearchParams(normalized);
      this.saleToggle.checked = params.get('on_sale') === '1';
    }

    /**
     * Applies client-side "on sale only" filtering to current rendered results.
     * @param {string} searchParams - URL search string or params string.
     */
    applySaleOnlyFilter(searchParams) {
      const normalized = searchParams.startsWith('?') ? searchParams.slice(1) : searchParams;
      const params = new URLSearchParams(normalized);
      const saleOnly = params.get('on_sale') === '1';
      const resultItems = this.results.querySelectorAll('.js-pagination-result');

      resultItems.forEach((item) => {
        const productCard = item.querySelector('.card--product, .product_card_tpg');
        if (!productCard) {
          item.hidden = false;
          return;
        }

        if (!saleOnly) {
          item.hidden = false;
          return;
        }

        const isOnSale = item.querySelector('.price--on-sale, .product-label--sale, .card_sales_badge');
        item.hidden = !isOnSale;
      });

      this.togglePaginationForSaleFilter(saleOnly);
      this.toggleNoResultsMessage();
    }

    /**
     * Toggles pagination visibility for the custom on-sale filter mode.
     * @param {boolean} saleOnly - Whether on-sale-only mode is active.
     */
    togglePaginationForSaleFilter(saleOnly) {
      const paginations = this.results.querySelectorAll('.js-pagination');
      const shouldHidePagination = saleOnly;

      paginations.forEach((pagination) => {
        pagination.hidden = shouldHidePagination;
        const nav = pagination.closest('nav');
        if (nav) nav.hidden = shouldHidePagination;
      });
    }

    /**
     * Shows/hides the empty-state message when no product cards are visible.
     */
    toggleNoResultsMessage() {
      const emptyMessage = this.results.querySelector('.js-products-empty-message');
      if (!emptyMessage) return;

      const productItems = Array.from(this.results.querySelectorAll('.js-pagination-result')).filter((item) =>
        item.querySelector('.card--product, .product_card_tpg'));
      const visibleProductCount = productItems.filter((item) => !item.hidden).length;
      emptyMessage.hidden = visibleProductCount !== 0;
    }

    /**
     * Handles 'click' events on the filters.
     * @param {object} evt - Event object.
     */
    handleFiltersClick(evt) {
      const { target } = evt;
      const mobileCategoryRemove = target.closest('.js-mobile-category-remove');
      if (mobileCategoryRemove) {
        evt.preventDefault();
        evt.stopPropagation();
        this.applyFilters(new URL(mobileCategoryRemove.href, window.location.origin).searchParams.toString(), evt);
        return;
      }

      const clearAllButton = target.closest('.js-mobile-clear-all');
      if (clearAllButton) {
        evt.preventDefault();
        const clearUrl = clearAllButton.dataset.clearUrl;
        if (!clearUrl) {
          this.close();
          return;
        }
        this.applyFilters(new URL(clearUrl, window.location.origin).searchParams.toString(), evt)
          .finally(() => {
            this.closeMobileFilterPanel();
            this.close();
          });
        return;
      }

      if (!theme.mediaMatches.lg) {
        const filterToggle = target.closest('.filter__toggle');
        const filter = filterToggle?.closest('.filter');
        if (filter && filter.dataset.type !== 'sort') {
          evt.preventDefault();
          this.openMobileFilterPanel(filter);
          return;
        }
      }

      // Filter 'clear' button clicked.
      if (target.matches('.js-clear-filter')) {
        evt.preventDefault();
        this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
      }

      // Filter 'show more' button clicked.
      if (target.matches('.js-show-more')) {
        const filter = target.closest('.filter');
        target.remove();

        filter.querySelectorAll('li').forEach((el) => {
          el.classList.remove('js-hidden');
        });

        if (!this.expanded.includes(filter.id)) {
          this.expanded.push(filter.id);
        }
      }
    }

    /**
     * Handles 'click' events on the active filters.
     * @param {object} evt - Event object.
     */
    handleActiveFiltersClick(evt) {
      const activeFilterLink = evt.target.closest('a');
      if (!activeFilterLink) return;
      evt.preventDefault();
      this.applyFilters(new URL(activeFilterLink.href).searchParams.toString(), evt);
    }

    /**
     * Opens the selected mobile filter panel.
     * @param {HTMLElement} filter - Filter details element.
     */
    openMobileFilterPanel(filter) {
      const item = filter.closest('.facets__filter-item');
      if (!item) return;

      this.closeMobileFilterPanel();
      this.mobileActiveFilterItem = item;
      this.mobileActiveFilterItem.classList.add('is-mobile-active');
      this.classList.add('is-mobile-panel-open');
      filter.classList.remove('is-closing');
      const panel = filter.querySelector('.disclosure__panel');
      if (panel) {
        panel.hidden = false;
        panel.removeAttribute('style');
      }
      // Mobile panels use display-based navigation UI; avoid details-disclosure height transitions here
      // because the panel can be measured while hidden and end up stuck at height: 0.
      filter.open = true;
      if (panel) {
        requestAnimationFrame(() => {
          panel.removeAttribute('style');
        });
      }

      if (this.mobileTitle) {
        this.mobileTitle.textContent = this.getMobileFilterLabel(filter);
        this.mobileTitle.hidden = !this.mobileTitle.textContent;
      }
      if (this.mobileBackButton) this.mobileBackButton.hidden = false;
    }

    /**
     * Intercepts mobile summary clicks before details-disclosure handles them.
     * Prevents height-transition races with the custom mobile panel UI.
     * @param {MouseEvent} evt - Click event.
     */
    handleMobileFilterToggleCapture(evt) {
      if (theme.mediaMatches.lg) return;

      const target = evt.target;
      if (target.closest('.js-mobile-category-remove')) return;

      const filterToggle = target.closest('.filter__toggle');
      const filter = filterToggle?.closest('.filter');
      if (!filter || filter.dataset.type === 'sort') return;

      evt.preventDefault();
      evt.stopPropagation();
      this.openMobileFilterPanel(filter);
    }

    /**
     * Closes mobile filter panel and returns to category list.
     */
    closeMobileFilterPanel() {
      if (this.mobileActiveFilterItem) {
        const activeFilter = this.mobileActiveFilterItem.querySelector('.filter');
        if (activeFilter) {
          activeFilter.classList.remove('is-closing');
          activeFilter.open = false;
          const panel = activeFilter.querySelector('.disclosure__panel');
          if (panel) {
            panel.removeAttribute('style');
          }
        }
        this.mobileActiveFilterItem.classList.remove('is-mobile-active');
        this.mobileActiveFilterItem = null;
      }

      this.classList.remove('is-mobile-panel-open');
      if (this.mobileTitle) {
        this.mobileTitle.textContent = '';
        this.mobileTitle.hidden = true;
      }
      if (this.mobileBackButton) this.mobileBackButton.hidden = true;
    }

    /**
     * Handles back button click in mobile filter navigation.
     * @param {object} evt - Event object.
     */
    handleMobileBackClick(evt) {
      evt.preventDefault();
      this.closeMobileFilterPanel();
    }

    /**
     * Handles vendor search input within the vendor filter.
     * @param {object} evt - Input event object.
     */
    handleVendorSearchInput(evt) {
      this.filterVendorOptions(evt.target);
    }

    /**
     * Gets the visible label for a mobile filter panel.
     * @param {HTMLElement} filter - Filter details element.
     * @returns {string}
     */
    getMobileFilterLabel(filter) {
      const labelWrap = filter.querySelector('.filter__toggle-row > .flex-auto');
      if (!labelWrap) return '';

      const clone = labelWrap.cloneNode(true);
      clone.querySelector('.filter__mobile-active-values')?.remove();
      return clone.textContent.trim();
    }

    /**
     * Filters visible vendor options by search query.
     * @param {HTMLElement} input - Vendor search input.
     */
    filterVendorOptions(input) {
      const group = input.closest('.filter');
      if (!group) return;

      const query = input.value.trim().toLowerCase();
      const options = group.querySelectorAll('.js-vendor-filter-option');
      const empty = group.querySelector('.js-vendor-filter-empty');
      let visibleCount = 0;

      options.forEach((option) => {
        if (option.classList.contains('js-vendor-filter-option--all')) {
          option.hidden = false;
          return;
        }
        const label = option.dataset.filterLabel || '';
        const matches = !query || label.includes(query);
        option.hidden = !matches;
        if (matches) visibleCount += 1;
      });

      if (empty) empty.hidden = visibleCount !== 0;
    }

    /**
     * Syncs active label state for product type filter options.
     * @param {HTMLElement} filter - Filter group element.
     */
    syncProductTypeActiveLabels(filter) {
      const labels = filter.querySelectorAll('.filter__label--product-type');
      labels.forEach((label) => {
        const input = label.previousElementSibling;
        label.classList.toggle('is-active', Boolean(input?.checked));
      });
    }

    /**
     * Handles history changes (e.g. back button clicked).
     * @param {object} evt - Event object.
     */
    handleHistoryChange(evt) {
      if (evt.state !== null) {
        let searchParams = '';

        if (evt.state && evt.state.searchParams) {
          ({ searchParams } = evt.state);
        }

        this.applyFilters(searchParams, null, false);
      }
    }

    /**
     * Fetches the filtered/sorted page data and updates the current page.
     * @param {string} searchParams - Filter/sort search parameters.
     * @param {object} evt - Event object.
     * @param {boolean} [updateUrl=true] - Update url with the selected options.
     */
    async applyFilters(searchParams, evt, updateUrl = true) {
      try {
        const activeMobileFilterId = !theme.mediaMatches.lg && this.mobileActiveFilterItem
          ? this.mobileActiveFilterItem.querySelector('.filter')?.id
          : null;

        // Preserve the current element focus
        const activeElementId = document.activeElement.id;

        // Disable infinite scrolling.
        const customPagination = document.querySelector('custom-pagination');
        if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'true';

        // Set loading state.
        this.results.classList.add('is-loading');

        // Disable "Show X results" button until submission is complete.
        const closeBtn = this.querySelector('.js-close-drawer-mob');
        closeBtn.ariaDisabled = 'true';
        closeBtn.classList.add('is-loading');

        // Use Section Rendering API for the request, if possible.
        let fetchUrl = `${window.location.pathname}?${searchParams}`;
        if (this.form.dataset.filterSectionId) {
          fetchUrl += `&section_id=${this.form.dataset.filterSectionId}`;
        }

        // Cancel current fetch request. (Raises an exception)
        if (this.applyFiltersFetchAbortController) {
          this.applyFiltersFetchAbortController.abort('Request changed');
        }
        this.applyFiltersFetchAbortController = new AbortController();

        // Fetch filtered products markup.
        const response = await fetch(fetchUrl, {
          method: 'GET',
          signal: this.applyFiltersFetchAbortController.signal
        });

        if (response.ok) {
          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();

          // Restore UI state.
          this.form.querySelectorAll('details-disclosure > details').forEach((existingFilter) => {
            const target = tmpl.content.getElementById(existingFilter.id);
            if (target) {
              target.open = existingFilter.open;
            }
          });
          tmpl.content.querySelectorAll('#facets details-disclosure > details').forEach((newFilter) => {
            if (this.expanded.includes(newFilter.id)) {
              const hiddenElements = newFilter.querySelectorAll('.js-hidden');
              hiddenElements.forEach((listItem) => {
                listItem.classList.remove('js-hidden');
              });
              newFilter.querySelector('.filter__more')?.remove();
            }
          });

          // Update the filters.
          this.form.innerHTML = tmpl.content.getElementById('facets').innerHTML;

          // Update the label of the mobile filter button
          closeBtn.innerText = tmpl.content.querySelector('.js-close-drawer-mob').innerText;
          const newHeaderCount = tmpl.content.querySelector('.facets-title__count');
          const currentHeaderCount = this.querySelector('.facets-title__count');
          if (newHeaderCount && currentHeaderCount) {
            currentHeaderCount.innerText = newHeaderCount.innerText;
          }

          // Preserve the CSS class of the results
          const currentResultsUl = this.results.querySelector('ul');
          this.currentResultsClass = currentResultsUl ? this.results.querySelector('ul').getAttribute('class') : this.currentResultsClass;

          // Update the results.
          this.results.innerHTML = tmpl.content.getElementById('filter-results').innerHTML;

          // Set the CSS class of the results to what it was
          const newResultsUl = this.results.querySelector('ul');
          if (newResultsUl && this.currentResultsClass) newResultsUl.setAttribute('class', this.currentResultsClass);

          // Reinitialize re-rendered components.
          this.addElements();
          this.addListeners();
          this.syncSaleToggleFromSearch(searchParams);
          this.applySaleOnlyFilter(searchParams);
          this.toggleNoResultsMessage();

          if (!theme.mediaMatches.lg && activeMobileFilterId) {
            let filterSelector = '';
            if (window.CSS && window.CSS.escape) {
              filterSelector = `#${window.CSS.escape(activeMobileFilterId)}`;
            } else {
              filterSelector = `[id="${activeMobileFilterId.replace(/"/g, '\\"')}"]`;
            }
            const activeFilter = this.form.querySelector(filterSelector);
            if (activeFilter) {
              this.openMobileFilterPanel(activeFilter);
            } else {
              this.closeMobileFilterPanel();
            }
          }

          // Reinitialize any custom pagination
          if (customPagination && customPagination.reload) customPagination.reload();

          // Update the URL.
          if (updateUrl) FacetFilters.updateURL(searchParams);

          // Scroll to the top of the results if needed
          if (this.results.getBoundingClientRect().top < 0) {
            // If the header is sticky, compensate for it when scrolling to elements
            let headerHeight = 0;
            if (document.querySelector('store-header[data-is-sticky="true"]')) {
              headerHeight = Number.parseInt(
                getComputedStyle(this.parentElement)
                  .getPropertyValue('--header-height')
                  .replace('px', ''),
                10
              ) || 0;
            }
            window.scrollTo({
              top: this.results.getBoundingClientRect().top + window.scrollY - headerHeight - 45,
              behavior: 'smooth'
            });
          }

          // Enable the "Show X results" button
          closeBtn.classList.remove('is-loading');
          closeBtn.removeAttribute('aria-disabled');

          // Renable infinite scroll
          if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'false';

          // Focus on the element with the same ID in the new HTML
          if (activeElementId) document.getElementById(activeElementId)?.focus();

          // Broadcast the update for anything else to hook into
          document.dispatchEvent(new CustomEvent('on:facet-filters:updated'), { bubbles: true });
        }
      } catch (error) {
        console.warn(error); // eslint-disable-line
      } finally {
        this.results.classList.remove('is-loading');
      }
    }

    /**
     * Updates the url with the current filter/sort parameters.
     * @param {string} searchParams - Filter/sort parameters.
     */
    static updateURL(searchParams) {
      window.history.pushState(
        { searchParams },
        '',
        `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
      );
    }
  }

  customElements.define('facet-filters', FacetFilters);
}
